/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/no-multi-comp */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  LENTE_IDS,
  META_DIARIA,
  formatAdereMonthShort,
  formatMedia,
  getUnidadeLabel,
} from './commandCenterConstants';

const Kpi = ({ label, value, hint }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: 'block',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        fontWeight: 600,
        fontSize: '0.625rem',
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.25, mt: 0.25 }}>
      {value}
    </Typography>
    {hint ? (
      <Typography variant="caption" color="text.secondary" noWrap>
        {hint}
      </Typography>
    ) : null}
  </Box>
);

Kpi.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
};

Kpi.defaultProps = {
  hint: null,
};

const hasTextSelection = () => {
  if (typeof window === 'undefined') return false;
  const selection = window.getSelection();
  return Boolean(selection && String(selection).length > 0);
};

const DualCard = ({
  title,
  lenteId,
  metrics,
  loading,
  onEnter,
  interactive,
}) => {
  const N = metrics?.digitadoresCount;
  const abaixo = metrics?.abaixoMetaCount;
  const meta = metrics?.metaDiaria ?? META_DIARIA[lenteId];
  const unidade = getUnidadeLabel(lenteId);
  const volumeLabel = lenteId === LENTE_IDS.USO_MUTUO ? 'Postes' : 'Serviços';

  const handleEnter = () => {
    if (!interactive || !onEnter) return;
    if (hasTextSelection()) return;
    onEnter();
  };

  return (
    <Card
      variant="outlined"
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `Entrar na lente ${title}` : undefined}
      onClick={interactive ? handleEnter : undefined}
      onKeyDown={interactive ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleEnter();
        }
      } : undefined}
      sx={{
        width: { xs: 1, sm: 380 },
        flexShrink: 0,
        cursor: interactive ? 'pointer' : 'default',
        ...(interactive ? {
          transition: (theme) => theme.transitions.create(['border-color', 'background-color', 'box-shadow'], {
            duration: theme.transitions.duration.short,
          }),
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: (theme) => `${theme.palette.primary.main}08`,
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        } : {}),
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ mb: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Meta {meta}/{unidade === 'postes' ? 'dia' : 'dia útil'}
          </Typography>
        </Box>

        {loading ? (
          <Skeleton height={72} />
        ) : (
          <Stack spacing={1}>
            <Stack direction="row" spacing={3}>
              <Kpi
                label="Média / dia útil"
                value={formatMedia(metrics?.mediaDiaUtil)}
                hint={N != null ? `${N} digitadores` : null}
              />
              <Kpi
                label="Abaixo da meta"
                value={abaixo != null && N != null ? `${abaixo}/${N}` : '—'}
              />
            </Stack>
            <Divider />
            <Stack direction="row" spacing={2}>
              <Kpi label="NSIRs" value={metrics?.nsirs ?? '—'} />
              <Kpi
                label={volumeLabel}
                value={metrics?.servicosTotal != null
                  ? `${metrics.servicosDigitados ?? 0}/${metrics.servicosTotal}`
                  : '—'}
              />
              <Kpi label="Pendentes" value={metrics?.servicosPendentes ?? '—'} />
              <Kpi label="Com pendência" value={metrics?.digitadoresComPendencia ?? '—'} />
            </Stack>
            {metrics?.adere ? (
              <>
                <Divider />
                <Stack spacing={0.75}>
                  <Typography variant="caption" color="text.secondary">
                    {`${formatAdereMonthShort(metrics.adere.adereMonth)} (mês calendário)`}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Kpi
                      label="Pend. Adere"
                      value={metrics.adere.servicosPendentes ?? '—'}
                    />
                    {metrics.adere.emRiscoCount != null ? (
                      <Kpi
                        label="Em risco de prazo"
                        value={
                          (metrics.adere.digitadoresCount ?? N) != null
                            ? `${metrics.adere.emRiscoCount}/${metrics.adere.digitadoresCount ?? N}`
                            : metrics.adere.emRiscoCount
                        }
                      />
                    ) : null}
                  </Stack>
                </Stack>
              </>
            ) : null}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

DualCard.propTypes = {
  title: PropTypes.string.isRequired,
  lenteId: PropTypes.string.isRequired,
  metrics: PropTypes.object,
  loading: PropTypes.bool,
  onEnter: PropTypes.func,
  interactive: PropTypes.bool,
};

DualCard.defaultProps = {
  metrics: null,
  loading: false,
  onEnter: null,
  interactive: true,
};

const GeralDualCards = ({ mtbt, usoMutuo, loading, onEnterLente }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={1.5}
    useFlexGap
    flexWrap="wrap"
    alignItems="stretch"
  >
    <DualCard
      title="Inspeção MT/BT"
      lenteId={LENTE_IDS.MT_BT}
      metrics={mtbt}
      loading={loading}
      onEnter={() => onEnterLente(LENTE_IDS.MT_BT)}
    />
    <DualCard
      title="Uso mútuo"
      lenteId={LENTE_IDS.USO_MUTUO}
      metrics={usoMutuo}
      loading={loading}
      onEnter={() => onEnterLente(LENTE_IDS.USO_MUTUO)}
    />
  </Stack>
);

GeralDualCards.propTypes = {
  mtbt: PropTypes.object,
  usoMutuo: PropTypes.object,
  loading: PropTypes.bool,
  onEnterLente: PropTypes.func.isRequired,
};

GeralDualCards.defaultProps = {
  mtbt: null,
  usoMutuo: null,
  loading: false,
};

export { DualCard };

export default GeralDualCards;
